'use client';

import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, withRetry } from '@/lib/db/supabase';
import { useRouter, usePathname } from 'next/navigation';
import { toast } from '@/hooks/use-toast';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const debugLog = (category: string, message: string, data?: any) => {
  console.log(`[${new Date().toISOString()}] [${category}]`, message, data || '');
};

const getErrorMessage = (error: Error): string => {
  const message = error.message.toLowerCase();
  
  if (message.includes('invalid login credentials')) {
    return 'Email ou senha incorretos';
  }
  if (message.includes('email not confirmed')) {
    return 'Por favor, confirme seu email antes de fazer login';
  }
  if (message.includes('invalid email')) {
    return 'Email inválido';
  }
  if (message.includes('password')) {
    return 'A senha deve ter no mínimo 6 caracteres';
  }
  
  return 'Ocorreu um erro. Por favor, tente novamente.';
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const authListenerRef = useRef<any>(null);
  const lastSyncRef = useRef<number>(0);
  const currentUserIdRef = useRef<string | null>(null);

  const handleRedirect = async (path: string) => {
    if (pathname === path) return;
    try {
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
      router.replace(path);
    } catch (error) {
      console.error('Erro no redirecionamento:', error);
      window.location.href = path;
    }
  };

  const updateUser = (newUser: User | null) => {
    if (!newUser) {
      setUser(null);
      currentUserIdRef.current = null;
      return;
    }

    if (
      currentUserIdRef.current !== newUser.id ||
      user?.email !== newUser.email ||
      user?.user_metadata?.full_name !== newUser.user_metadata?.full_name
    ) {
      setUser(newUser);
      currentUserIdRef.current = newUser.id;
    }
  };

  const syncUserProfile = async (userId: string, userData: User) => {
    try {
      const now = Date.now();
      if (now - lastSyncRef.current < 5000) {
        debugLog('Profile', 'Sincronização ignorada - muito recente');
        return;
      }
      lastSyncRef.current = now;

      debugLog('Profile', 'Iniciando sincronização de perfil', {
        userId,
        email: userData.email
      });

      // Usar withRetry para garantir a sincronização
      const { error } = await withRetry(() => 
        Promise.resolve(supabase.rpc('update_user_profile_data', {
          user_id: userId,
          user_name: userData.user_metadata?.full_name || '',
          user_email: userData.email || '',
          user_phone: userData.phone || ''
        })).then(result => ({
          data: result.data,
          error: result.error
        })), 3, // max retries
        1000 // delay between retries
      );

      if (error) {
        debugLog('Profile', 'Erro na sincronização', error);
        throw error;
      }

      // Verificar se o perfil foi realmente atualizado
      const { data: profile, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, email')
        .eq('id', userId)
        .single();

      if (checkError || !profile) {
        throw new Error('Falha ao verificar perfil após sincronização');
      }

      debugLog('Profile', 'Sincronização concluída com sucesso');
    } catch (error) {
      debugLog('Profile', 'Erro na sincronização de perfil', error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível atualizar seu perfil. Tentando novamente...",
        variant: "destructive"
      });
      throw error;
    }
  };


  useEffect(() => {
    if (!authListenerRef.current) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        debugLog('Auth', `Evento de autenticação: ${event}`, {
          userId: session?.user?.id,
          event
        });
        
        if (event === 'SIGNED_IN' && session?.user) {
          // Primeiro atualizamos o usuário e sincronizamos o perfil
          updateUser(session.user);
          
          try {
            // Garante que a sessão está estabelecida e o perfil sincronizado
            await Promise.all([
              syncUserProfile(session.user.id, session.user),
              new Promise(resolve => setTimeout(resolve, 1000)), // Pequeno delay para garantir a propagação da sessão
              supabase.auth.getSession() // Confirma que a sessão está ativa
            ]);
            
            if (!pathname.startsWith('/dashboard')) {
              window.location.href = '/dashboard'; // Força um refresh ao redirecionar
            }
          } catch (error) {
            debugLog('Auth', 'Erro na sincronização inicial', error);
            toast({
              title: "Erro na sincronização",
              description: "Por favor, atualize a página"
            });
          }
        } else if (event === 'SIGNED_OUT') {
          updateUser(null);
          if (pathname.startsWith('/dashboard')) {
            await handleRedirect('/');
          }
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          updateUser(session.user);
          await syncUserProfile(session.user.id, session.user);
        }
      });

      authListenerRef.current = subscription;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        updateUser(session.user);
        syncUserProfile(session.user.id, session.user).then(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => {
      if (authListenerRef.current) {
        authListenerRef.current.unsubscribe();
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      debugLog('Login', 'Tentativa de login', { email });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        toast({
          title: "Erro no login",
          description: getErrorMessage(error),
          variant: "destructive"
        });
        throw error;
      }

      if (data.user) {
        debugLog('Login', 'Login bem-sucedido', {
          userId: data.user.id
        });
        
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo de volta!"
        });

        try {
          // Garante que a sessão está estabelecida e o perfil sincronizado
          await Promise.all([
            syncUserProfile(data.user.id, data.user),
            new Promise(resolve => setTimeout(resolve, 1000)), // Pequeno delay para garantir a propagação da sessão
            supabase.auth.getSession() // Confirma que a sessão está ativa
          ]);
          
          // Força um refresh completo ao redirecionar
          window.location.href = '/dashboard';
        } catch (error) {
          debugLog('Login', 'Erro na sincronização após login', error);
        }

      }
    } catch (error) {
      debugLog('Login', 'Erro no login', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        toast({
          title: "Erro no login com Google",
          description: "Não foi possível fazer login com sua conta Google",
          variant: "destructive"
        });
        throw error;
      }
    } catch (error) {
      debugLog('Login', 'Erro no login com Google', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        toast({
          title: "Erro no login com Apple",
          description: "Não foi possível fazer login com sua conta Apple",
          variant: "destructive"
        });
        throw error;
      }
    } catch (error) {
      debugLog('Login', 'Erro no login com Apple', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      debugLog('Logout', 'Iniciando logout');
      currentUserIdRef.current = null; // Limpa o ID do usuário atual
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        toast({
          title: "Erro ao fazer logout",
          description: "Não foi possível encerrar sua sessão",
          variant: "destructive"
        });
        throw error;
      }
      
      debugLog('Logout', 'Logout realizado com sucesso');
      toast({
        title: "Logout realizado",
        description: "Até logo!"
      });

      window.location.href = '/'; // Força um refresh completo da página
    } catch (error) {
      debugLog('Logout', 'Erro no logout', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login,
      loginWithGoogle,
      loginWithApple,
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
